import { useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { and, collection, onSnapshot, or, orderBy, query, Timestamp, where } from '@firebase/firestore';

import { useAuth } from '../../hooks';
import { db } from '../../firebase-config';
import { Banner } from '../admin/BannerManagement/constants.ts';
import { bannerConverter } from './bannerConverter.ts';

export const useTargetUserTypesBanners = () => {
  const { user, userType } = useAuth();
  const [banners, setBanners] = useState<Banner[]>([]);

  useEffect(() => {
    if (!user) {
      return () => {};
    }

    const now = Timestamp.now();

    const targetUserTypesQuery = query(
      collection(db, 'banners'),
      and(
        where('isActive', '==', true),
        where('targetUserTypes', 'array-contains-any', [userType, 'all']), // run again with 'all'
        or(where('startDate', '==', null), where('startDate', '<', now)),
        or(where('endDate', '==', null), where('endDate', '>', now)),
      ),
      orderBy('createdAt', 'desc'),
    ).withConverter(bannerConverter);

    // Set up listeners for both queries
    const unsubscribes = onSnapshot(
      targetUserTypesQuery,
      (snapshot) => {
        setBanners(() => snapshot.docs.map((doc) => doc.data()));
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error(error);
          console.error('Error in user type banner listener:', { targetUserTypesQuery, userType });
        }

        captureException(error, {
          tags: {
            issueIn: 'useTargetUserTypesBanners',
          },
          extra: {
            userType,
          },
        });
      },
    );

    // Clean up listeners on unmount
    return () => {
      unsubscribes();
    };
  }, [userType, user]);

  return banners;
};
